import { useState } from 'react';
import { Ban, Evidence } from '../types/player';
import { formatDistanceToNow } from 'date-fns';
import { CreatorProfile } from './CreatorProfile';
import { EvidenceSection } from './EvidenceSection';
import { TicketLinks } from './TicketLinks';
import { AddEvidenceModal } from './AddEvidenceModal';
import { ShieldAlert, Calendar, Globe, Server, UserCheck, UserX, Paperclip, Eye, Trash2, ShieldOff, Shield, Loader, Link2, Pencil, Gavel } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../utils/cn';
import { notesApiService } from '../services/notesApi';
import { ConfirmationModal } from './ConfirmationModal';
import { EditBanModal } from './EditBanModal';
import { PlayerAppealsSection } from './appeals/PlayerAppealsSection';

interface BanCardProps {
  ban: Ban;
  canManage: boolean;
  canDelete: boolean;
  canViewAppeals?: boolean;
  onDelete?: (banUuid: string) => void;
  onUpdate?: (updatedBan: Ban) => void;
  onLink?: () => void;
}

const ScopeIcon = ({ scope, target }: { scope: Ban['scope'], target: Ban['scope_target'] }) => {
    switch (scope) {
      case 'global': return <span title={`Global: ${target || 'All'}`}><Globe className="w-4 h-4" /></span>;
      case 'cluster': return <span title={`Cluster: ${target || 'All'}`}><Server className="w-4 h-4" /></span>;
      case 'fqdn': return <span title={`FQDN: ${target || 'All'}`}><Server className="w-4 h-4" /></span>;
      case 'universal': return <span title={`Universal: ${target || 'All'}`}><Globe className="w-4 h-4 text-purple-400" /></span>;
      default: return null;
    }
};

export const BanCard = ({ ban, canManage, canDelete, canViewAppeals = false, onDelete, onUpdate, onLink }: BanCardProps) => {
  const [currentBan, setCurrentBan] = useState(ban);
  const [isEvidenceVisible, setIsEvidenceVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [evidenceVersion, setEvidenceVersion] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [banForEditing, setBanForEditing] = useState<Ban | null>(null);

  const isPermanent = currentBan.expires === null;
  const isExpired = !isPermanent && new Date((currentBan.expires || 0) * 1000) < new Date();
  const isActive = currentBan.enforced === 1 && !isExpired;

  const handleUploadSuccess = (_newEvidence?: Evidence) => {
    setEvidenceVersion(v => v + 1);
    if (!isEvidenceVisible) setIsEvidenceVisible(true);
    setIsModalOpen(false);
  };

  const handleDelete = () => {
    if (onDelete) {
        onDelete(currentBan.ban_uuid);
    }
  };

  const handleEditBan = async (updatedReason: string, updatedExpires: number | null) => {
    setIsUpdating(true);
    try {
        const updatedBan = await notesApiService.updateBan(currentBan.ban_uuid, {
            reason: updatedReason,
            expires: updatedExpires,
        });
        setCurrentBan(updatedBan);
        if (onUpdate) {
            onUpdate(updatedBan);
        }
    } catch (error) {
        alert("Failed to update ban.");
    } finally {
        setIsUpdating(false);
        setBanForEditing(null);
    }
  };

  const handleToggleEnforced = async () => {
    setIsUpdating(true);
    try {
      const updatedBan = await notesApiService.updateBan(currentBan.ban_uuid, {
        enforced: currentBan.enforced === 1 ? 0 : 1,
      });
      setCurrentBan(updatedBan);
      if (onUpdate) {
        onUpdate(updatedBan);
      }
    } catch (error) {
      alert("Failed to update ban status.");
    } finally {
        setIsUpdating(false);
        setShowConfirmation(false);
    }
  };

  return (
    <>
      <div className={cn(
        "glass-card p-4 space-y-3",
        !isActive && "opacity-60"
      )}>
        <div className="flex justify-between items-start gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className={cn("icon-tile", isActive ? "icon-tile-danger" : "icon-tile-neutral")}>
              <ShieldAlert className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">Ban <span className="font-mono text-xs text-glass-400">#{currentBan.ban_uuid.substring(0,6)}</span></p>
              <div className="text-xs text-glass-400 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span>Banned by</span>
                <CreatorProfile creatorId={currentBan.banner_email} />
                <span className="tabular-nums">{formatDistanceToNow(new Date(currentBan.created * 1000), { addSuffix: true })}</span>
              </div>
            </div>
          </div>
          <span className={cn(
            "badge badge-dot shrink-0",
            isActive ? 'badge-danger' : 'badge-neutral'
          )}>
            {isActive ? 'Active' : isExpired ? 'Expired' : 'Not Enforced'}
          </span>
        </div>
        
        <p className="subcard p-3 text-sm text-white md:ml-12">{currentBan.reason}</p>

        <div className="md:ml-12 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-glass-400">
          <div className="flex items-center gap-1.5" title="Expires">
            <Calendar className="w-3.5 h-3.5" />
            <span className="tabular-nums">{isPermanent ? 'Permanent' : formatDistanceToNow(new Date(currentBan.expires! * 1000), { addSuffix: true })}</span>
          </div>
           <div className="flex items-center gap-1.5" title="Scope">
            <ScopeIcon scope={currentBan.scope} target={currentBan.scope_target} />
            <span>{currentBan.scope} {currentBan.scope_target && <span className="font-mono">({currentBan.scope_target})</span>}</span>
          </div>
           <div className="flex items-center gap-1.5" title="Appealable">
            {currentBan.appealable ? <UserCheck className="w-3.5 h-3.5 text-green-400"/> : <UserX className="w-3.5 h-3.5 text-red-400"/>}
            <span>{currentBan.appealable ? 'Appealable' : 'Not Appealable'}</span>
          </div>
        </div>
        
        <div className="border-t border-glass-200/10 mt-3 pt-3 flex flex-col lg:flex-row lg:justify-between gap-2">
           <div className="flex flex-wrap items-center gap-2">
             {canManage && (
                <>
                    <button onClick={() => setShowConfirmation(true)} className="glass-button-secondary btn-xs touch-manipulation" disabled={isUpdating}>
                        {isUpdating ? <Loader className="w-3.5 h-3.5 animate-spin" /> : isActive ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                        {isUpdating ? 'Updating...' : isActive ? 'Lift Ban' : 'Re-enforce'}
                    </button>
                    <button onClick={() => setBanForEditing(currentBan)} className="glass-button-secondary btn-xs touch-manipulation" title="Edit Ban">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                </>
             )}
             {canDelete && (
                <button onClick={handleDelete} className="btn-danger btn-xs touch-manipulation">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
            )}
            {/* TODO: Add edit functionality if canManage is true */}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canViewAppeals && currentBan.appealable === 1 && (
              <Link to={`/appeals?user_uuid=${currentBan.user_uuid}`} className="glass-button-secondary btn-xs touch-manipulation" title="View ban appeals for this player">
                <Gavel className="w-3.5 h-3.5" /> Appeals
              </Link>
            )}
            <button onClick={onLink} className="glass-button-secondary btn-xs touch-manipulation" title="Link to another item">
                <Link2 className="w-3.5 h-3.5" /> Link Item
            </button>
            <button onClick={() => setIsModalOpen(true)} className="glass-button-secondary btn-xs touch-manipulation">
              <Paperclip className="w-3.5 h-3.5" /> Add Evidence
            </button>
            <button onClick={() => setIsEvidenceVisible(!isEvidenceVisible)} className="glass-button-secondary btn-xs touch-manipulation" aria-expanded={isEvidenceVisible}>
              <Eye className="w-3.5 h-3.5" /> {isEvidenceVisible ? 'Hide' : 'Show'} Details
            </button>
          </div>
        </div>
        
        {isEvidenceVisible && (
            <div className="space-y-4 border-t border-glass-200/10 pt-4 mt-4">
                <TicketLinks itemId={currentBan.ban_uuid} itemType="ban" />
                <EvidenceSection key={evidenceVersion} itemId={currentBan.ban_uuid} itemType="ban" />
                {canViewAppeals && currentBan.appealable === 1 && (
                  <PlayerAppealsSection userUuid={currentBan.user_uuid} />
                )}
            </div>
        )}
      </div>
      
      {isModalOpen && (
        <AddEvidenceModal
          itemId={currentBan.ban_uuid}
          itemType='ban'
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {showConfirmation && (
        <ConfirmationModal
            title={isActive ? 'Confirm Ban Lift' : 'Confirm Ban Re-enforcement'}
            message={`Are you sure you want to ${isActive ? 'lift' : 're-enforce'} this ban?`}
            confirmText={isActive ? 'Lift Ban' : 'Re-enforce'}
            onConfirm={handleToggleEnforced}
            onClose={() => setShowConfirmation(false)}
            isLoading={isUpdating}
        />
      )}

    {banForEditing && (
        <EditBanModal
            ban={banForEditing}
            onClose={() => setBanForEditing(null)}
            onSave={handleEditBan}
            isLoading={isUpdating}
        />
    )}
    </>
  );
};
